import type {
  OnyxComponentInputs,
  OnyxTableColumn,
  OnyxTime,
} from '@onyx/angular';
import { Duration } from 'luxon';
import { AddressCellComponent } from '../../../../common/components/cells/address-cell/address-cell.component';
import { AmountCellComponent } from '../../../../common/components/cells/amount-cell/amount-cell.component';
import { DateCellComponent } from '../../../../common/components/cells/date-cell/date-cell.component';
import { FlagsCellComponent } from '../../../../common/components/cells/flags-cell/flags-cell.component';
import { LanguageCellComponent } from '../../../../common/components/cells/language-cell/language-cell.component';
import { NumberCellComponent } from '../../../../common/components/cells/number-cell/number-cell.component';
import { ParametersCellComponent } from '../../../../common/components/cells/parameters-cell/parameters-cell.component';
import { StringCellComponent } from '../../../../common/components/cells/string-cell/string-cell.component';
import { TimeCellComponent } from '../../../../common/components/cells/time-cell/time-cell.component';
import { UnitCellComponent } from '../../../../common/components/cells/unit-cell/unit-cell.component';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { ContractorCellComponent } from '../../../management-panel/contractors/common/cells/contractor-cell/contractor-cell.component';
import { OrderFtlCellComponent } from '../components/cells/order-ftl-cell/order-ftl-cell.component';
import { OrderSemiTrailerSizeCellComponent } from '../components/cells/order-semi-trailer-size-cell/order-semi-trailer-size-cell.component';
import type { Order } from '../interfaces/order';
import type { OrderImportData } from './orders-import-schema';

type OrderImportDataColumns = OnyxTableColumn<
  OrderImportData & { index: number }
>;

export const getOrderImportColumns = (): OrderImportDataColumns[] => {
  const getHandlingPointColumns = (
    point: keyof Pick<OrderImportData, 'loadingPoint' | 'unloadingPoint'>,
  ): OrderImportDataColumns[] => {
    const category = point === 'loadingPoint' ? 'loading' : 'unloading';
    return [
      {
        id: `${point}Address`,
        name: `orders.importList.${category}Address`,
        width: '200px',
        component: {
          ref: AddressCellComponent,
          inputs: ({ item }): OnyxComponentInputs<AddressCellComponent> => ({
            address: item[point].address,
            format: 'full',
          }),
        },
      },
      {
        id: `${point}ServiceTime`,
        name: `forms.serviceTime.${category}`,
        width: '100px',
        component: {
          ref: TimeCellComponent,
          inputs: ({ item }): OnyxComponentInputs<TimeCellComponent> => ({
            time: `${Duration.fromObject({ seconds: item[point].serviceTime }).toFormat('hh:mm')}:00` as OnyxTime,
          }),
        },
      },
      {
        id: `${point}Type`,
        name: `orders.orderForm.points.term.${category}`,
        width: '150px',
        component: {
          ref: StringCellComponent,
          inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
            value: item[point].timeWindows.type,
            dictionaryCode: DictionaryCode.ORDER_TIME_WINDOW_TYPE,
          }),
        },
      },
      {
        id: `${point}Date`,
        name: `orders.importList.${category}Date`,
        width: '150px',
        component: {
          ref: DateCellComponent,
          inputs: ({ item }): OnyxComponentInputs<DateCellComponent> => ({
            date: item[point].timeWindows.windows[0].date,
          }),
        },
      },
      {
        id: `${point}Fix`,
        name: 'labels.fix',
        width: '100px',
        component: {
          ref: TimeCellComponent,
          inputs: ({ item }): OnyxComponentInputs<TimeCellComponent> => ({
            time: item[point].timeWindows.windows[0].time,
          }),
        },
      },
      {
        id: `${point}Interval`,
        name: 'labels.interval',
        width: '150px',
        component: {
          ref: TimeCellComponent,
          inputs: ({ item }): OnyxComponentInputs<TimeCellComponent> => ({
            time: item[point].timeWindows.windows[0].timeRange,
          }),
        },
      },
      {
        id: `${point}ReferenceNumber`,
        name: `orders.importList.${category}ReferenceNumber`,
        width: '150px',
        component: {
          ref: StringCellComponent,
          inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
            value: item[point].referenceNumber,
          }),
        },
      },
      {
        id: `${point}DriverNote`,
        name: `orders.importList.${category}DriverNote`,
        width: '150px',
        component: {
          ref: StringCellComponent,
          inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
            value: item[point].driverNote,
          }),
        },
      },
    ];
  };

  return [
    {
      required: true,
      id: 'index',
      name: 'labels.row',
      width: '100px',
      component: {
        ref: NumberCellComponent,
        inputs: (data): OnyxComponentInputs<NumberCellComponent> => ({
          value: data.item.index,
        }),
      },
    },

    /* basicInformation */
    {
      id: 'sender',
      name: 'labels.principal',
      width: '200px',
      component: {
        ref: ContractorCellComponent,
        inputs: ({ item }): OnyxComponentInputs<ContractorCellComponent> => ({
          companyProfile: item.sender.companyProfile,
          companyData: item.sender.companyData,
          showSince: false,
        }),
      },
    },
    {
      id: 'senderBranch',
      name: 'labels.principalBranch',
      width: '200px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.senderBranch,
        }),
      },
    },
    {
      id: 'ourBranch',
      name: 'labels.ourBranch',
      width: '200px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.ourBranch,
        }),
      },
    },
    {
      id: 'cargoSpace',
      name: 'labels.cargoSpace',
      width: '150px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.basicInformation.vehicleTypes,
          dictionaryCode: DictionaryCode.VEHICLE_TYPE,
        }),
      },
    },
    {
      id: 'minSemiTrailerSize',
      name: '',
      tooltip: 'labels.minSemiTrailerSize',
      icon: { name: 'semi-trailer-filled', size: 20 },
      align: 'center',
      width: '48px',
      component: {
        ref: OrderSemiTrailerSizeCellComponent,
        inputs: ({
          item,
        }): OnyxComponentInputs<OrderSemiTrailerSizeCellComponent> => ({
          semiTrailerSize: item.basicInformation.minSemiTrailerSize,
        }),
      },
    },
    {
      id: 'ftl',
      name: '',
      width: '48px',
      tooltip: 'FTL/LTL',
      component: {
        ref: OrderFtlCellComponent,
        inputs: ({ item }): OnyxComponentInputs<OrderFtlCellComponent> => ({
          isDedicated: item.parameters.isDedicated,
        }),
      },
    },
    {
      id: 'dischargeType',
      name: 'labels.dischargeType',
      width: '150px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.basicInformation.dischargeTypes,
          dictionaryCode: DictionaryCode.DISCHARGE_TYPE,
        }),
      },
    },
    {
      id: 'orderReferenceNumber',
      name: 'labels.orderReferenceNumber',
      width: '150px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.basicInformation.referenceNumber,
        }),
      },
    },
    {
      id: 'billing',
      name: 'labels.billing',
      width: '150px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.basicInformation.rateType,
          dictionaryCode: DictionaryCode.RATE_TYPE,
        }),
      },
    },
    {
      id: 'rate',
      name: 'labels.rate',
      width: '150px',
      component: {
        ref: AmountCellComponent,
        inputs: ({ item }): OnyxComponentInputs<AmountCellComponent> => ({
          amount: item.basicInformation.rate,
          decimalPlaces: 2,
        }),
      },
    },
    {
      id: 'vatRate',
      name: 'labels.vat',
      width: '100px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.basicInformation.vatRate,
          dictionaryCode: DictionaryCode.VAT_RATE,
        }),
      },
    },
    {
      id: 'countries',
      name: 'labels.countries',
      width: '100px',
      component: {
        ref: FlagsCellComponent,
        inputs: ({ item }): OnyxComponentInputs<FlagsCellComponent> => ({
          flags: item?.route?.summary?.countries,
        }),
      },
    },
    {
      id: 'officeNote',
      name: 'labels.officeNote',
      width: '150px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.note?.content,
        }),
      },
    },

    /* parameters */
    {
      id: 'parameters',
      name: 'labels.parameters',
      width: 'fill',
      component: {
        ref: ParametersCellComponent,
        inputs: (data): OnyxComponentInputs<ParametersCellComponent> => ({
          additionalParameters: data.item.parameters as Order['parameters'],
        }),
      },
    },

    ...getHandlingPointColumns('loadingPoint'),

    /* good */
    {
      id: 'goodType',
      name: 'labels.goodType',
      width: '150px',
      component: {
        ref: LanguageCellComponent,
        inputs: ({ item }): OnyxComponentInputs<LanguageCellComponent> => ({
          value: item.good.type.names,
        }),
      },
    },
    {
      id: 'goodReferenceNumber',
      name: 'labels.goodReferenceNumber',
      width: '150px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.good.referenceNumber,
        }),
      },
    },
    {
      id: 'unit',
      name: 'labels.unit',
      width: '150px',
      component: {
        ref: LanguageCellComponent,
        inputs: ({ item }): OnyxComponentInputs<LanguageCellComponent> => ({
          value: item.good.unit.names,
        }),
      },
    },
    {
      id: 'goodQuantity',
      name: 'labels.quantity',
      width: '100px',
      component: {
        ref: NumberCellComponent,
        inputs: ({ item }): OnyxComponentInputs<NumberCellComponent> => ({
          value: item.good.quantity,
        }),
      },
    },
    {
      id: 'totalWeight',
      name: 'labels.totalWeight',
      width: '150px',
      component: {
        ref: UnitCellComponent,
        inputs: ({ item }): OnyxComponentInputs<UnitCellComponent> => ({
          value: item.good.totalWeight,
          decimalPlaces: 2,
          unit: 'kg',
        }),
      },
    },
    {
      id: 'goodsSizeType',
      name: 'labels.size',
      width: '150px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.good.size?.type,
          dictionaryCode: DictionaryCode.GOODS_SIZE_TYPE,
        }),
      },
    },
    {
      id: 'loadingMeters',
      name: 'labels.loadingMeters',
      width: '150px',
      component: {
        ref: NumberCellComponent,
        inputs: ({ item }): OnyxComponentInputs<NumberCellComponent> => ({
          value: item.good.size?.loadingMeters,
          decimalPlaces: 2,
        }),
      },
    },
    {
      id: 'length',
      name: 'labels.length',
      width: '100px',
      component: {
        ref: UnitCellComponent,
        inputs: ({ item }): OnyxComponentInputs<UnitCellComponent> => ({
          value: item.good.size?.length,
          decimalPlaces: 2,
          unit: 'm',
        }),
      },
    },
    {
      id: 'width',
      name: 'labels.width',
      width: '100px',
      component: {
        ref: UnitCellComponent,
        inputs: ({ item }): OnyxComponentInputs<UnitCellComponent> => ({
          value: item.good.size?.width,
          decimalPlaces: 2,
          unit: 'm',
        }),
      },
    },
    {
      id: 'height',
      name: 'labels.height',
      width: '100px',
      component: {
        ref: UnitCellComponent,
        inputs: ({ item }): OnyxComponentInputs<UnitCellComponent> => ({
          value: item.good.size?.height,
          decimalPlaces: 2,
          unit: 'm',
        }),
      },
    },
    {
      id: 'volume',
      name: 'labels.volume',
      width: '100px',
      component: {
        ref: UnitCellComponent,
        inputs: ({ item }): OnyxComponentInputs<UnitCellComponent> => ({
          value: item.good.size?.volume,
          decimalPlaces: 2,
          unit: 'm³',
        }),
      },
    },
    {
      id: 'unitedNationsNumber',
      name: 'labels.unitedNations',
      width: '150px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.good.unitedNationsNumber,
        }),
      },
    },
    {
      id: 'statisticalNumber',
      name: 'labels.statisticalNumber',
      width: '150px',
      component: {
        ref: StringCellComponent,
        inputs: ({ item }): OnyxComponentInputs<StringCellComponent> => ({
          value: item.good.statisticalNumber,
        }),
      },
    },

    ...getHandlingPointColumns('unloadingPoint'),
  ];
};
